import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { AM_BEST_RATING_INFO, AM_BEST_CARRIER_RATINGS } from '@/util/amBestRatingData';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { PRODUCTS } from '@client/lib/nyc-lif-demo';

interface QuoteProductInfoProps {
  carrierName: string;
  productName: string;
  showLogo?: boolean;
  showRating?: boolean;
  className?: string;
}

/**
 * Reusable component for displaying quote product information
 * Includes carrier logo, product name, and optional AM Best rating
 */
export function QuoteProductInfo({
  carrierName,
  productName,
  showLogo = true,
  showRating = true,
  className = '',
}: QuoteProductInfoProps) {
  const { flags } = useFeatureFlags();
  const logo = flags.nycLifeDemo
    ? `/logos/nyl-logo.png`
    : `/logos/${carrierName.toLowerCase()}.svg`;
  const rating = AM_BEST_CARRIER_RATINGS[carrierName];

  return (
    <div className={`flex items-start gap-4 ${className}`}>
      {showLogo && (
        <div className="border-primary/20 size-20 shrink-0 select-none rounded-lg border p-2">
          <img
            src={logo}
            alt={carrierName}
            className="size-full"
          />
        </div>
      )}
      <div className="mr-8 flex max-w-[240px] flex-col gap-1">
        {showRating && rating && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-sm font-medium text-sky-600">Credit Rating&nbsp;{rating}</div>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm rounded-lg border-0 bg-black/90 px-4 py-3.5 shadow-none">
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-bold text-white">
                    {AM_BEST_RATING_INFO[rating]?.title}
                  </p>
                  <p className="text-sm text-white/80">
                    {AM_BEST_RATING_INFO[rating]?.description}
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <h2 className="text-primary-dark break-words text-base font-medium leading-snug sm:text-lg">
          {flags.nycLifeDemo ? (PRODUCTS as any)?.[carrierName] : productName}
        </h2>
        <div
          data-testid="quote-card-carrier"
          className="text-primary-dark/70 text-sm"
        >
          {flags.nycLifeDemo ? 'New York Life' : carrierName}
        </div>
      </div>
    </div>
  );
}
